import { useCallback, useMemo, useState } from 'react';
import { headerApi } from 'app/services/headerApi';
import type { IReleaseNote } from 'models';

const {
  useGetReleaseNotesAllQuery,
  useGetReleaseNotesQuery,
  useMarkReleaseNoteReadMutation,
} = headerApi;

/**
 * Release Notes dropdown (§3.A "Release Notes dropdown" / parity 114).
 *
 * Markup mirrors the Backbone `#release_notes_wrapper` + `release_notes.tmpl`:
 *   div#release_notes_wrapper[.non-clickable when empty]
 *     ├── a.release-notes.with-list[.active when unread recent notes]
 *     └── div#release_notes_list.nav-list[.hide]
 *         ├── .notes-heading "Release Notes"
 *         ├── .notes-tabs > div[data-tab=recently_deployed|work_in_progres]
 *         ├── ul#recently_deployed   (Past 90 Days; notes_li[.read])
 *         └── ul#work_in_progres     (90-Day Forecast Estimate; always .read)
 *
 * Behavior:
 *  - All notes from GET /user_settings/release-note-all/; read-state from
 *    GET /user_settings/release-note/ (ReadedNotes).
 *  - splitDatesByToday: itemDate <= today → recent (pastOrToday), else upcoming
 *    (future, sorted ascending).
 *  - Empty all-notes → wrapper `.non-clickable`.
 *  - Tabs switch the visible list (changeNoteTabs).
 *  - Default active tab = recently_deployed when any recent note exists, else
 *    work_in_progres.
 *  - Recent (not-read) note click → mark read (POST {release_note_id}), add
 *    `.read`, recompute the green `.active` badge (any recent unread remaining).
 *  - Bell turns green (`.active`) when there is an unread recent note.
 */

type Tab = 'recently_deployed' | 'work_in_progres';

interface ReleaseNotesDropdownProps {
  open: boolean;
  onToggle: () => void;
}

/** splitDatesByToday — header.js (normalized to local midnight). */
const splitDatesByToday = (list: IReleaseNote[]) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const pastOrToday: IReleaseNote[] = [];
  const future: IReleaseNote[] = [];
  list.forEach((item) => {
    const d = new Date(item.release_note_date);
    d.setHours(0, 0, 0, 0);
    if (d <= today) pastOrToday.push(item);
    else future.push(item);
  });
  future.sort(
    (a, b) =>
      new Date(a.release_note_date).getTime() - new Date(b.release_note_date).getTime()
  );
  return { pastOrToday, future };
};

const ReleaseNotesDropdown = ({ open, onToggle }: ReleaseNotesDropdownProps) => {
  const { data: allNotes = [] } = useGetReleaseNotesAllQuery();
  const { data: readNotes = [] } = useGetReleaseNotesQuery();
  const [markRead] = useMarkReleaseNoteReadMutation();

  // Locally-marked-read ids layered over the server read-state.
  const [localRead, setLocalRead] = useState<Set<number>>(new Set());

  const serverReadIds = useMemo(
    () => new Set(readNotes.map((r) => r.release_note_id)),
    [readNotes]
  );
  const isRead = useCallback(
    (id: number) => serverReadIds.has(id) || localRead.has(id),
    [serverReadIds, localRead]
  );

  const { pastOrToday, future } = useMemo(() => splitDatesByToday(allNotes), [allNotes]);
  const pastOrTodayExist = pastOrToday.length > 0;
  const futureExist = future.length > 0;

  // Default active tab (header.tmpl): recently_deployed if recent notes exist,
  // else work_in_progres.
  const [tab, setTab] = useState<Tab | null>(null);
  const activeTab: Tab =
    tab ?? (pastOrTodayExist ? 'recently_deployed' : 'work_in_progres');

  const empty = allNotes.length < 1;

  // Green badge: any RECENT note still unread.
  const hasUnread = pastOrToday.some((n) => !isRead(n.id));

  const onMarkRead = useCallback(
    (note: IReleaseNote) => {
      if (isRead(note.id)) return;
      setLocalRead((s) => new Set(s).add(note.id));
      markRead({ release_note_id: note.id });
    },
    [isRead, markRead]
  );

  const renderNote = (note: IReleaseNote, recent: boolean) => {
    const read = recent ? isRead(note.id) : true;
    return (
      <li
        key={note.id}
        id={`notes_${note.id}`}
        className={`notes_li list-li-row${read ? ' read' : ''}`}
        data-id={note.id}
        onClick={recent && !read ? () => onMarkRead(note) : undefined}
      >
        <span className="desc">{note.release_note}</span>
        <span className="date">{note.release_note_date}</span>
        {note.description && (
          <div className="note-description-tooltip">
            <div className="note-description-body">{note.description}</div>
          </div>
        )}
      </li>
    );
  };

  return (
    <div id="release_notes_wrapper" className={empty ? 'non-clickable' : undefined}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        className={`with-padding-2 release-notes with-list ka-has-tooltip${
          hasUnread ? ' active' : ''
        }`}
        tabIndex={-1}
        data-for="release_notes_list"
        tooltip="Release Notes"
        role="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (empty) return;
          onToggle();
        }}
      >
        <i className="material-symbols-outlined">&#xe616;</i>
      </a>
      <div
        id="release_notes_list"
        className={`nav-list${open && !empty ? '' : ' hide'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="notes-heading">Release Notes</div>
        <div className="notes-tabs">
          <div
            className={`${!pastOrTodayExist ? 'hide' : ''}${
              activeTab === 'recently_deployed' ? ' active' : ''
            }`}
            data-tab="recently_deployed"
            role="button"
            tabIndex={-1}
            onClick={() => setTab('recently_deployed')}
          >
            Recent Deployments
            <span>Past 90 Days</span>
          </div>
          <div
            className={`${!futureExist ? 'hide' : ''}${
              activeTab === 'work_in_progres' ? ' active' : ''
            }`}
            data-tab="work_in_progres"
            role="button"
            tabIndex={-1}
            onClick={() => setTab('work_in_progres')}
          >
            Upcoming Deployments
            {/* release_notes.tmpl:11 (dev f2e8006db9 renamed it) */}
            <span>90-Day Forecast Estimate</span>
          </div>
        </div>

        <ul
          id="recently_deployed"
          className={activeTab === 'recently_deployed' ? 'active' : 'hide'}
        >
          {pastOrToday.map((n) => renderNote(n, true))}
        </ul>
        <ul
          id="work_in_progres"
          className={activeTab === 'work_in_progres' ? 'active' : 'hide'}
        >
          {future.map((n) => renderNote(n, false))}
        </ul>

        <div className="close-btn-wrapper">
          <button
            id="notes-close-btn"
            className="btn close-list"
            data-for="release_notes_list"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReleaseNotesDropdown;
